// PublishModal.jsx - Système de publication intelligent en 3 étapes + Brouillons Cloud
// Étape 1: Choix du profil (Pro/Particulier) + Accès aux brouillons
// Étape 2: Options selon le profil
// Étape 3: Formulaire ou écran de connexion
// Sync: localStorage pour non-connectés, API cloud pour connectés

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  X, ArrowLeft, Camera, Video, HardHat, Users, Calendar, 
  Briefcase, MessageSquare, Search, FileText, Home, Building2,
  Upload, Plus, Check, AlertCircle, UserPlus, LogIn, Save, Clock,
  Trash2, Edit3, ChevronRight, Cloud, CloudOff, RefreshCw, AlertTriangle, Star
} from 'lucide-react';
import { toast } from 'sonner';

// Clé localStorage pour les brouillons
const DRAFTS_STORAGE_KEY = 'abracadabati_publish_drafts';
import { API_URL } from '../../config/api';

// Liste des 16 métiers principaux + catégories complètes
const METIERS = [
  'Plombier', 'Électricien', 'Maçon', 'Carreleur', 'Peintre', 'Menuisier',
  'Couvreur', 'Chauffagiste', 'Pisciniste', 'Paysagiste', 'Climaticien',
  'Façadier', 'Serrurier', 'Vitrier', 'Charpentier', 'Architecte', 'Géomètre',
  'Expert bâtiment', 'Bureau d\'études', 'Diagnostiqueur', 'Maître d\'œuvre', 'Autre'
];

// Catégories métiers complètes pour le réseau thématique
const CATEGORIES_METIERS = [
  { id: 'all', label: 'Tous les métiers', icon: '🏠' },
  { id: 'plomberie', label: 'Plomberie', icon: '🔧' },
  { id: 'electricite', label: 'Électricité', icon: '⚡' },
  { id: 'maconnerie', label: 'Maçonnerie', icon: '🧱' },
  { id: 'carrelage', label: 'Carrelage', icon: '🔲' },
  { id: 'peinture', label: 'Peinture', icon: '🎨' },
  { id: 'menuiserie', label: 'Menuiserie', icon: '🪵' },
  { id: 'couverture', label: 'Couverture / Toiture', icon: '🏠' },
  { id: 'chauffage', label: 'Chauffage / Climatisation', icon: '🌡️' },
  { id: 'piscine', label: 'Piscine / Spa', icon: '🏊' },
  { id: 'jardin', label: 'Jardin / Paysage', icon: '🌳' },
  { id: 'facade', label: 'Façade / Isolation', icon: '🏗️' },
  { id: 'serrurerie', label: 'Serrurerie / Métallerie', icon: '🔐' },
  { id: 'vitrerie', label: 'Vitrerie / Miroiterie', icon: '🪟' },
  { id: 'charpente', label: 'Charpente / Ossature bois', icon: '🪓' },
  { id: 'domotique', label: 'Domotique / Smart Home', icon: '📱' },
  { id: 'renovation', label: 'Rénovation générale', icon: '🔨' },
  { id: 'decoration', label: 'Décoration intérieure', icon: '🛋️' },
  { id: 'cuisine', label: 'Cuisine / Salle de bain', icon: '🍳' },
  { id: 'terrassement', label: 'Terrassement / VRD', icon: '🚜' },
  { id: 'architecture', label: 'Architecture', icon: '📐' },
  { id: 'expertise', label: 'Expertise bâtiment', icon: '🔍' },
  { id: 'bureau_etudes', label: 'Bureau d\'études', icon: '📊' },
  { id: 'diagnostic', label: 'Diagnostic immobilier', icon: '📋' },
  { id: 'maitrise_oeuvre', label: 'Maîtrise d\'œuvre', icon: '👷' },
  { id: 'geometre', label: 'Géomètre-Expert', icon: '📏' },
];

// Humeurs / Sentiments pour les posts
const HUMEURS = [
  { id: 'fier', emoji: '💪', label: 'Fier de moi' },
  { id: 'heureux', emoji: '😊', label: 'Heureux' },
  { id: 'motive', emoji: '🔥', label: 'Motivé' },
  { id: 'fatigue', emoji: '😅', label: 'Fatigué mais satisfait' },
  { id: 'excite', emoji: '🎉', label: 'Excité' },
  { id: 'concentre', emoji: '🎯', label: 'Concentré' },
  { id: 'inspire', emoji: '💡', label: 'Inspiré' },
  { id: 'reconnaissant', emoji: '🙏', label: 'Reconnaissant' },
  { id: 'defi', emoji: '💎', label: 'En mode défi' },
  { id: 'apprentissage', emoji: '📚', label: 'En apprentissage' },
];

// Types de contenu pour les posts
const TYPES_CONTENU = [
  { id: 'avant_apres', label: 'Avant / Après', icon: '🔄', description: 'Transformation complète' },
  { id: 'chantier_cours', label: 'Chantier en cours', icon: '🏗️', description: 'Work in progress' },
  { id: 'astuce', label: 'Astuce / Conseil', icon: '💡', description: 'Partager un savoir' },
  { id: 'question', label: 'Question', icon: '❓', description: 'Demander de l\'aide' },
  { id: 'realisation', label: 'Réalisation terminée', icon: '✅', description: 'Projet fini' },
  { id: 'materiel', label: 'Matériel / Outillage', icon: '🛠️', description: 'Test ou avis' },
  { id: 'inspiration', label: 'Inspiration', icon: '✨', description: 'Idées et tendances' },
  { id: 'fail', label: 'Fail / Erreur', icon: '😬', description: 'Apprendre des erreurs' },
];

// Import des catégories BTP unifiées
import { CATEGORIES_BTP, getCategoryEmoji } from '../../data/categories-btp';

// Catégories pour les petites annonces (basées sur les rayons Marketplace)
const ANNONCE_CATEGORIES = CATEGORIES_BTP.map(cat => ({
  id: cat.id,
  label: cat.label,
  icon: cat.emoji,
  subcategories: cat.subcategories,
}));

// États des annonces
const ANNONCE_CONDITIONS = [
  { id: 'neuf', label: 'Neuf' },
  { id: 'comme_neuf', label: 'Comme neuf' },
  { id: 'tres_bon', label: 'Très bon état' },
  { id: 'bon', label: 'Bon état' },
  { id: 'satisfaisant', label: 'État satisfaisant' },
  { id: 'pour_pieces', label: 'Pour pièces / À réparer' },
];

// Options menu Pro (8 cards)
const PRO_OPTIONS = [
  { id: 'post', icon: '📸', title: 'Photo / Post', subtitle: 'Réalisation, conseil, actualité', bg: '#EEF2F8', form: 'post' },
  { id: 'reel', icon: '🎬', title: 'Reel', subtitle: 'Vidéo courte 5-10 secondes', bg: '#FFF0E6', form: 'reel' },
  { id: 'chantier', icon: '🏗️', title: 'Chantier en cours', subtitle: "Partage l'avancement", bg: '#EEF2F8', form: 'chantier' },
  { id: 'soustraitant', icon: '🤝', title: 'Recherche sous-traitant', subtitle: "Besoin urgent d'un pro", bg: '#FFF0E6', form: 'soustraitant' },
  { id: 'evenement', icon: '📅', title: 'Événement', subtitle: 'Formation, réunion, salon', bg: '#EEF2F8', form: 'evenement' },
  { id: 'emploi', icon: '💼', title: "Offre d'emploi", subtitle: 'Recruter un profil BTP', bg: '#FFF0E6', redirect: '/emploi' },
  { id: 'salon', icon: '🛋️', title: 'Salon thématique', subtitle: 'Créer un groupe métier', bg: '#EEF2F8', redirect: '/reseau/salons' },
  { id: 'devis', icon: '📊', title: 'Créer un devis', subtitle: 'Accès direct au Cockpit', bg: '#1E3A5F', special: true, redirect: '/batiassist' },
];

// Options menu Particulier (9 cards) - Réorganisé avec Petite annonce en premier
const PARTICULIER_OPTIONS = [
  { id: 'annonce', icon: '📢', title: 'Petite annonce', subtitle: 'Vente matériaux, outils', bg: '#FFF5EC', form: 'annonce' },
  { id: 'post', icon: '📸', title: 'Photo / Post', subtitle: 'Mon chantier, mes travaux', bg: '#FEF3E8', form: 'post' },
  { id: 'video', icon: '🎥', title: 'Vidéo', subtitle: 'Vidéo de mes travaux', bg: '#FFF5EC', form: 'video' },
  { id: 'chantier', icon: '🏗️', title: 'Chantier en cours', subtitle: 'Auto-construction, rénovation', bg: '#FEF3E8', form: 'chantier' },
  { id: 'recherche', icon: '🔍', title: "Recherche d'artisan", subtitle: 'Je cherche un professionnel', bg: '#FFF5EC', form: 'recherche' },
  { id: 'emploi', icon: '💼', title: "Offre d'emploi", subtitle: 'Je cherche quelqu\'un', bg: '#FEF3E8', form: 'emploi' },
  { id: 'conseil', icon: '❓', title: 'Demande de conseil', subtitle: 'Question à la communauté', bg: '#FFF5EC', form: 'conseil' },
  { id: 'evenement', icon: '🎉', title: 'Événement', subtitle: 'Créer ou partager un événement', bg: '#FEF3E8', form: 'evenement' },
  { id: 'salon', icon: '🛋️', title: 'Salon thématique', subtitle: 'Rejoindre ou créer un groupe', bg: '#FFF5EC', form: 'salon' },
];

// ============ API HELPERS ============

const getAuthToken = () => {
  return localStorage.getItem('ecosystem_token') || localStorage.getItem('buyer_token') || localStorage.getItem('user_token');
};

// Fetch drafts from cloud API
const fetchCloudDrafts = async () => {
  const token = getAuthToken();
  if (!token) return null;
  
  try {
    const response = await fetch(`${API_URL}/api/drafts`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) return null;
    const data = await response.json();
    return data.drafts || [];
  } catch {
    return null;
  }
};

// Save draft to cloud API
const saveCloudDraft = async (draft) => {
  const token = getAuthToken();
  if (!token) return null;
  
  try {
    const response = await fetch(`${API_URL}/api/drafts`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        id: draft.id,
        profile_type: draft.profileType,
        form_type: draft.formType,
        post_text: draft.postText,
        selected_metier: draft.selectedMetier,
        destination: draft.destination,
        chantier_name: draft.chantierName,
        chantier_city: draft.chantierCity,
        chantier_progress: draft.chantierProgress,
        search_radius: draft.searchRadius,
        event_date: draft.eventDate,
        event_time: draft.eventTime,
        event_online: draft.eventOnline,
      })
    });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
};

// Delete draft from cloud API
const deleteCloudDraft = async (draftId) => {
  const token = getAuthToken();
  if (!token) return false;
  
  try {
    const response = await fetch(`${API_URL}/api/drafts/${draftId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return response.ok;
  } catch {
    return false;
  }
};

// ============ LOCAL STORAGE HELPERS ============

const getLocalDrafts = () => {
  try {
    const drafts = localStorage.getItem(DRAFTS_STORAGE_KEY);
    return drafts ? JSON.parse(drafts) : [];
  } catch {
    return [];
  }
};

const saveLocalDraft = (draft) => {
  const drafts = getLocalDrafts();
  const newDraft = {
    ...draft,
    id: draft.id || Date.now().toString(),
    updatedAt: new Date().toISOString(),
    createdAt: draft.createdAt || new Date().toISOString(),
  };
  
  const existingIndex = drafts.findIndex(d => d.id === newDraft.id);
  if (existingIndex >= 0) {
    drafts[existingIndex] = newDraft;
  } else {
    drafts.unshift(newDraft);
  }
  
  const limitedDrafts = drafts.slice(0, 10);
  localStorage.setItem(DRAFTS_STORAGE_KEY, JSON.stringify(limitedDrafts));
  return newDraft;
};

const deleteLocalDraft = (draftId) => {
  const drafts = getLocalDrafts().filter(d => d.id !== draftId);
  localStorage.setItem(DRAFTS_STORAGE_KEY, JSON.stringify(drafts));
};

// Convert cloud draft to local format
const cloudToLocalFormat = (cloudDraft) => ({
  id: cloudDraft.id,
  profileType: cloudDraft.profile_type,
  formType: cloudDraft.form_type,
  postText: cloudDraft.post_text,
  selectedMetier: cloudDraft.selected_metier,
  destination: cloudDraft.destination,
  chantierName: cloudDraft.chantier_name,
  chantierCity: cloudDraft.chantier_city,
  chantierProgress: cloudDraft.chantier_progress,
  searchRadius: cloudDraft.search_radius,
  eventDate: cloudDraft.event_date,
  eventTime: cloudDraft.event_time,
  eventOnline: cloudDraft.event_online,
  createdAt: cloudDraft.created_at,
  updatedAt: cloudDraft.updated_at,
  expiresAt: cloudDraft.expires_at,
  daysUntilExpiry: cloudDraft.days_until_expiry,
  isExpiringSoon: cloudDraft.is_expiring_soon,
});

// Formater la date relative
const formatRelativeDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return "À l'instant";
  if (diffMins < 60) return `Il y a ${diffMins} min`;
  if (diffHours < 24) return `Il y a ${diffHours}h`;
  if (diffDays < 7) return `Il y a ${diffDays}j`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

// Obtenir le titre du type de formulaire
const getFormTitle = (formType) => {
  const titles = {
    post: 'Photo / Post',
    video: 'Vidéo',
    annonce: 'Petite annonce',
    chantier: 'Chantier en cours',
    soustraitant: 'Recherche sous-traitant',
    evenement: 'Événement',
    recherche: "Recherche d'artisan",
    conseil: 'Demande de conseil',
    emploi: "Offre d'emploi",
    salon: 'Salon thématique',
  };
  return titles[formType] || 'Publication';
};

export default function PublishModal({ isOpen, onClose, isLoggedIn = false, skipProfileChoice = false, defaultProfile = 'particulier' }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(skipProfileChoice ? 2 : 1);
  const [profileType, setProfileType] = useState(skipProfileChoice ? defaultProfile : null); // 'pro' ou 'particulier'
  const [activeForm, setActiveForm] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showDrafts, setShowDrafts] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [isCloudSync, setIsCloudSync] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [showSubscribePrompt, setShowSubscribePrompt] = useState(false);
  const modalRef = useRef(null);
  const touchStartY = useRef(0);

  // Form states
  const [postText, setPostText] = useState('');
  const [photos, setPhotos] = useState([]);
  const [selectedMetier, setSelectedMetier] = useState('');
  const [destination, setDestination] = useState('social');
  const [showProductLink, setShowProductLink] = useState(false);
  const [chantierName, setChantierName] = useState('');
  const [chantierCity, setChantierCity] = useState('');
  const [chantierProgress, setChantierProgress] = useState(50);
  const [searchRadius, setSearchRadius] = useState(30);
  const [eventDate, setEventDate] = useState('');
  const [eventTime, setEventTime] = useState('');
  const [eventOnline, setEventOnline] = useState(false);
  
  // Nouveaux états pour les options enrichies
  const [contentType, setContentType] = useState('');
  const [selectedMood, setSelectedMood] = useState('');
  const [taggedPeople, setTaggedPeople] = useState('');
  const [postLocation, setPostLocation] = useState('');
  
  // États pour le formulaire Petite annonce
  const [annonceTitle, setAnnonceTitle] = useState('');
  const [annoncePrice, setAnnoncePrice] = useState('');
  const [annonceCondition, setAnnonceCondition] = useState('');
  const [annonceCategory, setAnnonceCategory] = useState('');
  const [annonceSubcategory, setAnnonceSubcategory] = useState('');
  const [annonceNegotiable, setAnnonceNegotiable] = useState(false);
  const [annonceDelivery, setAnnonceDelivery] = useState(false);
  const [annoncePhotos, setAnnoncePhotos] = useState([]); // Array of {file, preview}
  const [annonceRemiseOptions, setAnnonceRemiseOptions] = useState({
    mainPropre: true,      // Remise en main propre
    envoi: false,          // Envoi possible
    fraisPort: '',         // Frais de port si envoi
    pointRelais: false,    // Point relais
  });
  const photoInputRef = useRef(null);
  
  // Obtenir les sous-catégories de la catégorie sélectionnée
  const currentSubcategories = annonceCategory 
    ? ANNONCE_CATEGORIES.find(c => c.id === annonceCategory)?.subcategories || []
    : [];

  // Handler pour upload photos annonce
  const handleAnnoncePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + annoncePhotos.length > 10) {
      toast.error('Maximum 10 photos autorisées');
      return;
    }
    
    const newPhotos = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      id: Date.now() + Math.random().toString(36).substr(2, 9)
    }));
    
    setAnnoncePhotos(prev => [...prev, ...newPhotos]);
  };

  // Supprimer une photo
  const removeAnnoncePhoto = (photoId) => {
    setAnnoncePhotos(prev => {
      const photo = prev.find(p => p.id === photoId);
      if (photo?.preview) {
        URL.revokeObjectURL(photo.preview);
      }
      return prev.filter(p => p.id !== photoId);
    });
  };

  // Réordonner les photos (drag & drop simplifié)
  const movePhotoToFirst = (photoId) => {
    setAnnoncePhotos(prev => {
      const photoIndex = prev.findIndex(p => p.id === photoId);
      if (photoIndex > 0) {
        const newPhotos = [...prev];
        const [photo] = newPhotos.splice(photoIndex, 1);
        newPhotos.unshift(photo);
        return newPhotos;
      }
      return prev;
    });
  };

  // Charger les brouillons (cloud si connecté, sinon local)
  const loadDrafts = useCallback(async () => {
    if (isLoggedIn) {
      setIsSyncing(true);
      const cloudDrafts = await fetchCloudDrafts();
      if (cloudDrafts) {
        const formattedDrafts = cloudDrafts.map(cloudToLocalFormat);
        setDrafts(formattedDrafts);
        setIsCloudSync(true);
        
        // Notification si des brouillons expirent bientôt
        const expiringSoon = formattedDrafts.filter(d => d.isExpiringSoon);
        if (expiringSoon.length > 0) {
          toast.warning(`${expiringSoon.length} brouillon${expiringSoon.length > 1 ? 's' : ''} expire${expiringSoon.length > 1 ? 'nt' : ''} bientôt !`, {
            description: 'Publie-les avant qu\'ils ne soient supprimés',
            icon: <AlertTriangle size={18} className="text-amber-500" />,
            duration: 5000,
          });
        }
      } else {
        // Fallback to local
        setDrafts(getLocalDrafts());
        setIsCloudSync(false);
      }
      setIsSyncing(false);
    } else {
      setDrafts(getLocalDrafts());
      setIsCloudSync(false);
    }
  }, [isLoggedIn]);

  // Charger les brouillons au montage
  useEffect(() => {
    if (isOpen) {
      loadDrafts();
    }
  }, [isOpen, loadDrafts]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setStep(skipProfileChoice ? 2 : 1);
        setProfileType(skipProfileChoice ? defaultProfile : null);
        setActiveForm(null);
        setSelectedOption(null);
        setShowDrafts(false);
        setCurrentDraftId(null);
        setShowSubscribePrompt(false);
        resetFormFields();
      }, 200);
    }
  }, [isOpen, skipProfileChoice, defaultProfile]);

  const resetFormFields = () => {
    setPostText('');
    setPhotos([]);
    setSelectedMetier('');
    setDestination('social');
    setShowProductLink(false);
    setChantierName('');
    setChantierCity('');
    setChantierProgress(50);
    setSearchRadius(30);
    setEventDate('');
    setEventTime('');
    setEventOnline(false);
    setContentType('');
    setSelectedMood('');
    setTaggedPeople('');
    setPostLocation('');
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Handle click outside
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle swipe down on mobile
  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    const touchY = e.touches[0].clientY;
    const diff = touchY - touchStartY.current;
    if (diff > 100) {
      onClose();
    }
  };

  // Sélection profil
  const handleProfileSelect = (type) => {
    setProfileType(type);
    setStep(2);
  };

  // Sélection option
  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    
    if (option.redirect) {
      onClose();
      navigate(option.redirect);
    } else if (option.form) {
      setActiveForm(option.form);
      setStep(3);
    }
  };

  // Retour
  const handleBack = () => {
    if (showDrafts) {
      setShowDrafts(false);
    } else if (step === 3) {
      setActiveForm(null);
      setSelectedOption(null);
      setCurrentDraftId(null);
      resetFormFields();
      setStep(2);
    } else if (step === 2) {
      setProfileType(null);
      setStep(1);
    }
  };

  // Sauvegarder comme brouillon (cloud si connecté, sinon local)
  const handleSaveDraft = async () => {
    const draftData = {
      id: currentDraftId,
      profileType,
      formType: activeForm,
      postText,
      selectedMetier,
      destination,
      chantierName,
      chantierCity,
      chantierProgress,
      searchRadius,
      eventDate,
      eventTime,
      eventOnline,
    };
    
    setIsSyncing(true);
    
    if (isLoggedIn) {
      // Sauvegarder dans le cloud
      const cloudResult = await saveCloudDraft(draftData);
      if (cloudResult) {
        setCurrentDraftId(cloudResult.id);
        await loadDrafts();
        toast.success('Brouillon synchronisé !', {
          description: 'Disponible sur tous tes appareils',
          icon: <Cloud size={18} className="text-blue-500" />,
        });
      } else {
        // Fallback to local
        const savedDraft = saveLocalDraft(draftData);
        setCurrentDraftId(savedDraft.id);
        setDrafts(getLocalDrafts());
        toast.success('Brouillon enregistré localement', {
          description: 'Sync cloud non disponible',
          icon: <CloudOff size={18} className="text-gray-400" />,
        });
      }
    } else {
      // Sauvegarder localement
      const savedDraft = saveLocalDraft(draftData);
      setCurrentDraftId(savedDraft.id);
      setDrafts(getLocalDrafts());
      toast.success('Brouillon enregistré !', {
        description: 'Connecte-toi pour synchroniser',
        icon: <Save size={18} />,
      });
    }
    
    setIsSyncing(false);
  };

  // Charger un brouillon
  const handleLoadDraft = (draft) => {
    setProfileType(draft.profileType);
    setActiveForm(draft.formType);
    setPostText(draft.postText || '');
    setSelectedMetier(draft.selectedMetier || '');
    setDestination(draft.destination || 'social');
    setChantierName(draft.chantierName || '');
    setChantierCity(draft.chantierCity || '');
    setChantierProgress(draft.chantierProgress || 50);
    setSearchRadius(draft.searchRadius || 30);
    setEventDate(draft.eventDate || '');
    setEventTime(draft.eventTime || '');
    setEventOnline(draft.eventOnline || false);
    setCurrentDraftId(draft.id);
    setShowDrafts(false);
    setStep(3);
  };

  // Supprimer un brouillon (cloud si connecté, sinon local)
  const handleDeleteDraft = async (e, draftId) => {
    e.stopPropagation();
    
    if (isLoggedIn) {
      const deleted = await deleteCloudDraft(draftId);
      if (deleted) {
        await loadDrafts();
      } else {
        // Fallback local
        deleteLocalDraft(draftId);
        setDrafts(getLocalDrafts());
      }
    } else {
      deleteLocalDraft(draftId);
      setDrafts(getLocalDrafts());
    }
    
    toast.success('Brouillon supprimé');
  };

  // Vérifier si l'utilisateur est un abonné (a un token valide)
  const isSubscriber = Boolean(localStorage.getItem('ecosystem_token') || localStorage.getItem('buyer_token'));
  
  // Publier
  const handlePublish = async () => {
    // Si pas abonné, afficher le prompt d'abonnement
    if (!isSubscriber) {
      setShowSubscribePrompt(true);
      return;
    }
    
    // Utilisateur abonné : publier réellement
    setIsPublishing(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const userId = localStorage.getItem('ecosystem_user_id') || 'demo_user';
      
      let postData = {
        user_id: userId,
        type: selectedOption?.id || 'post',
        content: postText,
        photos: photos,
        profile_type: profileType,
        // Nouveaux champs enrichis
        metier_category: selectedMetier,
        content_type: contentType,
        mood: selectedMood,
        tagged_people: taggedPeople,
        location: postLocation,
      };
      
      // Ajouter les données spécifiques selon le type
      if (selectedOption?.id === 'chantier') {
        postData = {
          ...postData,
          chantier_name: chantierName,
          chantier_city: chantierCity,
          chantier_progress: chantierProgress,
        };
      } else if (selectedOption?.id === 'artisan') {
        postData = {
          ...postData,
          metier: selectedMetier,
          search_radius: searchRadius,
        };
      } else if (selectedOption?.id === 'evenement' || selectedOption?.id === 'salon') {
        postData = {
          ...postData,
          event_date: eventDate,
          event_time: eventTime,
          event_online: eventOnline,
        };
      }
      
      const response = await fetch(`${API_URL}/api/cockpit/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(postData)
      });
      
      if (response.ok) {
        // Supprimer le brouillon si on publie
        if (currentDraftId) {
          deleteDraft(currentDraftId);
        }
        toast.success('Publication envoyée avec succès !');
        onClose();
      } else {
        toast.error('Erreur lors de la publication');
      }
    } catch (error) {
      console.error('Erreur publication:', error);
      toast.error('Erreur lors de la publication');
    } finally {
      setIsPublishing(false);
    }
  };
  
  // Fermer le prompt et rediriger vers inscription
  const handleSubscribe = () => {
    onClose();
    navigate('/inscription');
  };

  const options = profileType === 'pro' ? PRO_OPTIONS : PARTICULIER_OPTIONS;
  const accentColor = profileType === 'pro' ? '#1E3A5F' : '#F97316';

  // Vérifier si le formulaire a du contenu
  const hasFormContent = postText || chantierName || selectedMetier || eventDate;

  if (!isOpen) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[9999] flex items-end md:items-center justify-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)' }}
          onClick={handleBackdropClick}
        >
          <motion.div
            ref={modalRef}
            initial={isMobile ? { y: '100%' } : { opacity: 0, y: 50, scale: 0.95 }}
            animate={isMobile ? { y: 0 } : { opacity: 1, y: 0, scale: 1 }}
            exit={isMobile ? { y: '100%' } : { opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="relative bg-white w-full md:max-w-[520px] max-h-[90vh] overflow-y-auto"
            style={{ 
              padding: '32px',
              borderRadius: isMobile ? '24px 24px 0 0' : '20px',
            }}
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
          >
            {/* Mobile pill indicator */}
            <div className="md:hidden absolute top-3 left-1/2 -translate-x-1/2 w-12 h-1.5 bg-gray-300 rounded-full" />

            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-5 w-10 h-10 flex items-center justify-center rounded-full text-gray-400 hover:text-[#1E3A5F] hover:bg-gray-100 transition-colors"
              data-testid="publish-modal-close"
            >
              <X size={24} />
            </button>

            {/* ═══════════════════════════════════════════════════════
                VUE BROUILLONS
            ═══════════════════════════════════════════════════════ */}
            {showDrafts && (
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                className="pt-4"
              >
                <div className="flex items-center gap-3 mb-4">
                  <button 
                    onClick={handleBack} 
                    className="w-10 h-10 flex items-center justify-center rounded-full text-gray-400 hover:text-[#1E3A5F] hover:bg-gray-100 transition-colors"
                  >
                    <ArrowLeft size={22} />
                  </button>
                  <h2 className="text-[18px] font-bold text-[#1E3A5F]">
                    Mes brouillons
                  </h2>
                  <span className="ml-auto px-2.5 py-1 bg-gray-100 rounded-full text-xs font-semibold text-gray-600">
                    {drafts.length}/10
                  </span>
                </div>

                {/* Indicateur de synchronisation cloud */}
                <div className={`mb-4 px-3 py-2 rounded-lg flex items-center gap-2 text-xs ${
                  isCloudSync ? 'bg-blue-50 text-blue-700' : 'bg-gray-50 text-gray-500'
                }`}>
                  {isSyncing ? (
                    <>
                      <RefreshCw size={14} className="animate-spin" />
                      <span>Synchronisation...</span>
                    </>
                  ) : isCloudSync ? (
                    <>
                      <Cloud size={14} />
                      <span>Synchronisé sur tous tes appareils</span>
                    </>
                  ) : (
                    <>
                      <CloudOff size={14} />
                      <span>Stockage local uniquement</span>
                      {!isLoggedIn && (
                        <button 
                          onClick={() => { onClose(); navigate('/connexion'); }}
                          className="ml-auto text-[#F97316] font-semibold hover:underline"
                        >
                          Se connecter
                        </button>
                      )}
                    </>
                  )}
                </div>

                {drafts.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                      <FileText size={28} className="text-gray-400" />
                    </div>
                    <p className="text-gray-500 font-medium">Aucun brouillon</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Tes publications en cours apparaîtront ici
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {drafts.map((draft) => (
                      <button
                        key={draft.id}
                        onClick={() => handleLoadDraft(draft)}
                        className="w-full flex items-center gap-3 p-4 rounded-xl border border-gray-200 hover:border-[#F97316] hover:bg-[#FFF8F4] transition-all text-left group"
                        data-testid={`draft-item-${draft.id}`}
                      >
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                          style={{ 
                            backgroundColor: draft.profileType === 'pro' ? '#EEF2F8' : '#FFF5EC' 
                          }}
                        >
                          {draft.formType === 'post' && '📸'}
                          {draft.formType === 'video' && '🎥'}
                          {draft.formType === 'annonce' && '📢'}
                          {draft.formType === 'chantier' && '🏗️'}
                          {draft.formType === 'soustraitant' && '🤝'}
                          {draft.formType === 'evenement' && '📅'}
                          {draft.formType === 'recherche' && '🔍'}
                          {draft.formType === 'conseil' && '❓'}
                          {draft.formType === 'emploi' && '💼'}
                          {draft.formType === 'salon' && '🛋️'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-[#1E3A5F] text-sm truncate">
                            {draft.chantierName || draft.postText?.substring(0, 40) || getFormTitle(draft.formType)}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            <span 
                              className="text-[10px] font-medium px-1.5 py-0.5 rounded"
                              style={{
                                backgroundColor: draft.profileType === 'pro' ? '#1E3A5F' : '#F97316',
                                color: 'white'
                              }}
                            >
                              {draft.profileType === 'pro' ? 'Pro' : 'Particulier'}
                            </span>
                            {/* Badge d'expiration */}
                            {draft.isExpiringSoon && (
                              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 flex items-center gap-0.5">
                                <AlertTriangle size={9} />
                                {draft.daysUntilExpiry === 0 ? 'Expire aujourd\'hui' : `${draft.daysUntilExpiry}j restants`}
                              </span>
                            )}
                            <span className="text-[11px] text-gray-400 flex items-center gap-1">
                              <Clock size={10} />
                              {formatRelativeDate(draft.updatedAt)}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={(e) => handleDeleteDraft(e, draft.id)}
                            className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                            data-testid={`draft-delete-${draft.id}`}
                          >
                            <Trash2 size={16} />
                          </button>
                          <ChevronRight size={18} className="text-gray-300 group-hover:text-[#F97316] transition-colors" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* ═══════════════════════════════════════════════════════
                ÉTAPE 1 — Choix du profil + Accès brouillons
            ═══════════════════════════════════════════════════════ */}
            {step === 1 && !showDrafts && (
              <motion.div
                initial={{ opacity: 0, x: 0 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                className="pt-4"
              >
                <h2 className="text-center text-[20px] font-bold text-[#1E3A5F] mb-2">
                  Tu publies en tant que ?
                </h2>
                <p className="text-center text-[13px] text-gray-500 mb-7">
                  Choisis ton profil pour voir les options adaptées
                </p>

                <div className="grid grid-cols-2 gap-4">
                  {/* Card Pro */}
                  <button
                    onClick={() => handleProfileSelect('pro')}
                    className="p-6 rounded-2xl border-2 text-center transition-all duration-150 hover:border-[#1E3A5F] hover:bg-[#F5F7FB]"
                    style={{
                      borderColor: '#e5e7eb',
                      backgroundColor: '#FFFFFF',
                    }}
                    data-testid="publish-profile-pro"
                  >
                    <div className="w-14 h-14 mx-auto mb-3 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#1E3A5F' }}>
                      <Briefcase size={28} className="text-white" />
                    </div>
                    <h3 className="text-[15px] font-bold text-[#1E3A5F] mb-1">Professionnel</h3>
                    <p className="text-[12px] text-gray-500">Artisan, entreprise BTP</p>
                  </button>

                  {/* Card Particulier */}
                  <button
                    onClick={() => handleProfileSelect('particulier')}
                    className="p-6 rounded-2xl border-2 text-center transition-all duration-150 hover:border-[#F97316] hover:bg-[#FFF8F2]"
                    style={{
                      borderColor: '#e5e7eb',
                      backgroundColor: '#FFFFFF',
                    }}
                    data-testid="publish-profile-particulier"
                  >
                    <div className="w-14 h-14 mx-auto mb-3 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#F97316' }}>
                      <Home size={28} className="text-white" />
                    </div>
                    <h3 className="text-[15px] font-bold text-[#1E3A5F] mb-1">Particulier</h3>
                    <p className="text-[12px] text-gray-500">Propriétaire, auto-constructeur</p>
                  </button>
                </div>

                {/* Bouton Mes Brouillons */}
                {drafts.length > 0 && (
                  <button
                    onClick={() => setShowDrafts(true)}
                    className="mt-6 w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 border-dashed border-gray-300 text-gray-600 hover:border-[#F97316] hover:text-[#F97316] hover:bg-[#FFF8F4] transition-all"
                    data-testid="open-drafts-btn"
                  >
                    <Edit3 size={18} />
                    <span className="font-medium">Reprendre un brouillon</span>
                    <span className="ml-1 px-2 py-0.5 bg-[#F97316] text-white text-xs font-bold rounded-full">
                      {drafts.length}
                    </span>
                  </button>
                )}
              </motion.div>
            )}

            {/* ═══════════════════════════════════════════════════════
                ÉTAPE 2 — Menu options (grille 2 colonnes, 8 cards)
            ═══════════════════════════════════════════════════════ */}
            {step === 2 && !showDrafts && (
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                className="pt-4"
              >
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                  <button 
                    onClick={handleBack} 
                    className="w-10 h-10 flex items-center justify-center rounded-full text-gray-400 hover:text-[#1E3A5F] hover:bg-gray-100 transition-colors"
                  >
                    <ArrowLeft size={22} />
                  </button>
                  <h2 className="text-[18px] font-bold text-[#1E3A5F] flex-1">
                    Que veux-tu publier ?
                  </h2>
                  <span 
                    className="px-3 py-1.5 rounded-full text-[11px] font-semibold"
                    style={{
                      backgroundColor: profileType === 'pro' ? '#1E3A5F' : '#F97316',
                      color: '#FFFFFF',
                    }}
                  >
                    {profileType === 'pro' ? 'Pro' : 'Particulier'}
                  </span>
                </div>

                {/* Options grid - 2 colonnes */}
                <div className="grid grid-cols-2 gap-2.5">
                  {options.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => handleOptionSelect(option)}
                      className="flex flex-col items-center gap-2 p-4 rounded-xl border transition-all duration-150 text-center group"
                      style={{
                        backgroundColor: option.special ? '#1E3A5F' : '#FFFFFF',
                        borderColor: option.special ? '#1E3A5F' : '#e5e7eb',
                      }}
                      onMouseEnter={(e) => {
                        if (!option.special) {
                          e.currentTarget.style.borderColor = accentColor;
                          e.currentTarget.style.backgroundColor = option.bg;
                          e.currentTarget.style.transform = 'scale(1.02)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!option.special) {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.backgroundColor = '#FFFFFF';
                          e.currentTarget.style.transform = 'scale(1)';
                        }
                      }}
                      data-testid={`publish-option-${option.id}`}
                    >
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                        style={{ backgroundColor: option.special ? 'rgba(255,255,255,0.15)' : option.bg }}
                      >
                        {option.icon}
                      </div>
                      <div>
                        <h4 
                          className="text-[13px] font-semibold"
                          style={{ color: option.special ? '#FFFFFF' : '#1E3A5F' }}
                        >
                          {option.title}
                        </h4>
                        <p 
                          className="text-[10px] mt-0.5"
                          style={{ color: option.special ? '#93B4D4' : '#6b7280' }}
                        >
                          {option.subtitle}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* ═══════════════════════════════════════════════════════
                ÉTAPE 3 — Formulaire (si connecté) ou Login prompt (si non connecté)
            ═══════════════════════════════════════════════════════ */}
            {step === 3 && !showDrafts && (
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                className="pt-4"
              >
                {/* ═══════════════════════════════════════════════════
                    NON CONNECTÉ → Écran d'invitation à se connecter
                ═══════════════════════════════════════════════════ */}
                {!isLoggedIn ? (
                  <div 
                    className="text-center py-8 px-4 -mx-8 -mb-8 rounded-b-[20px] md:rounded-b-[20px] relative"
                    style={{ 
                      backgroundColor: '#1E3A5F',
                      marginTop: '-32px',
                      paddingTop: '48px',
                    }}
                  >
                    {/* Close button override for dark bg */}
                    <button
                      onClick={onClose}
                      className="absolute top-4 right-5 w-10 h-10 flex items-center justify-center rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-colors z-10"
                      data-testid="publish-modal-close-dark"
                    >
                      <X size={24} />
                    </button>

                    {/* Back button */}
                    <button
                      onClick={handleBack}
                      className="absolute top-4 left-5 w-10 h-10 flex items-center justify-center rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-colors z-10"
                      data-testid="publish-back-dark"
                    >
                      <ArrowLeft size={22} />
                    </button>

                    <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-white/10 flex items-center justify-center">
                      <UserPlus size={40} className="text-white" />
                    </div>

                    <h2 className="text-2xl font-bold text-white mb-3">
                      Connecte-toi pour publier
                    </h2>
                    <p className="text-white/70 text-sm mb-8 max-w-xs mx-auto">
                      Crée un compte gratuit ou connecte-toi pour partager avec la communauté
                    </p>

                    <div className="space-y-3 max-w-xs mx-auto">
                      <button
                        onClick={() => {
                          onClose();
                          navigate('/inscription');
                        }}
                        className="w-full py-4 bg-[#F97316] hover:bg-[#EA580C] text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2"
                        data-testid="publish-create-account-btn"
                      >
                        <UserPlus size={20} />
                        Créer mon compte
                      </button>

                      <button
                        onClick={() => {
                          onClose();
                          navigate('/connexion');
                        }}
                        className="w-full py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 border border-white/20"
                        data-testid="publish-login-btn"
                      >
                        <LogIn size={20} />
                        Me connecter
                      </button>
                    </div>
                  </div>
                ) : (
                  /* ═══════════════════════════════════════════════════
                      CONNECTÉ → Formulaire de publication
                  ═══════════════════════════════════════════════════ */
                  <>
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-5">
                      <button onClick={handleBack} className="w-10 h-10 flex items-center justify-center rounded-full text-gray-400 hover:text-[#1E3A5F] hover:bg-gray-100 transition-colors">
                        <ArrowLeft size={22} />
                      </button>
                      <h2 className="text-[18px] font-bold text-[#1E3A5F] flex-1">
                        {activeForm === 'post' && 'Nouveau post'}
                        {activeForm === 'video' && 'Nouvelle vidéo'}
                        {activeForm === 'annonce' && 'Nouvelle petite annonce'}
                        {activeForm === 'chantier' && 'Chantier en cours'}
                        {activeForm === 'soustraitant' && 'Recherche sous-traitant'}
                        {activeForm === 'evenement' && 'Nouvel événement'}
                        {activeForm === 'recherche' && "Recherche d'artisan"}
                        {activeForm === 'conseil' && 'Demande de conseil'}
                        {activeForm === 'emploi' && "Offre d'emploi"}
                        {activeForm === 'salon' && 'Salon thématique'}
                      </h2>
                      {/* Indicateur de brouillon */}
                      {currentDraftId && (
                        <span className="px-2 py-1 bg-amber-100 text-amber-700 text-[10px] font-semibold rounded-full flex items-center gap-1">
                          <Edit3 size={10} />
                          Brouillon
                        </span>
                      )}
                    </div>

                    {/* Form Content */}
                    <div className="space-y-4">
                      {/* FORMULAIRE POST / VIDÉO / CONSEIL */}
                      {(activeForm === 'post' || activeForm === 'video' || activeForm === 'conseil') && (
                        <>
                          {/* Zone texte */}
                          <div className="relative">
                            <textarea
                              value={postText}
                              onChange={(e) => setPostText(e.target.value.slice(0, 500))}
                              placeholder={
                                activeForm === 'conseil' 
                                  ? "Pose ta question à la communauté..."
                                  : "Partage ta réalisation, un conseil..."
                              }
                              className="w-full min-h-[100px] p-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F] resize-none text-sm"
                            />
                            <span className="absolute bottom-3 right-3 text-[11px] text-gray-400">
                              {postText.length}/500
                            </span>
                          </div>

                          {/* Upload zone */}
                          <div 
                            className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center bg-gray-50 cursor-pointer hover:border-gray-400 transition-colors"
                          >
                            <Upload size={32} className="mx-auto text-gray-400 mb-2" />
                            <p className="text-sm text-gray-600 font-medium">
                              {activeForm === 'video' ? 'Ajouter une vidéo' : 'Ajouter des photos'}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              {activeForm === 'video' ? 'Vidéo courte (max 60 secondes)' : 'Maximum 10 photos'}
                            </p>
                          </div>

                          {/* ═══════════════════════════════════════════════════════════════ */}
                          {/* ONGLETS OPTIONS ENRICHIES */}
                          {/* ═══════════════════════════════════════════════════════════════ */}
                          
                          <div className="bg-gray-50 rounded-xl p-4 space-y-4">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Options du post</p>
                            
                            {/* 1. CATÉGORIE MÉTIER - Le plus important */}
                            <div>
                              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                                <span className="text-base">🏗️</span>
                                Catégorie métier
                                <span className="text-red-500">*</span>
                              </label>
                              <select
                                value={selectedMetier}
                                onChange={(e) => setSelectedMetier(e.target.value)}
                                className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/20 text-sm bg-white"
                              >
                                <option value="">Sélectionner un métier</option>
                                {CATEGORIES_METIERS.map(cat => (
                                  <option key={cat.id} value={cat.id}>
                                    {cat.icon} {cat.label}
                                  </option>
                                ))}
                              </select>
                              <p className="text-xs text-gray-400 mt-1">
                                Permet aux utilisateurs de filtrer par métier
                              </p>
                            </div>

                            {/* 2. TYPE DE CONTENU */}
                            <div>
                              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                                <span className="text-base">📋</span>
                                Type de contenu
                              </label>
                              <div className="grid grid-cols-2 gap-2">
                                {TYPES_CONTENU.map(type => (
                                  <label
                                    key={type.id}
                                    className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-all ${
                                      contentType === type.id
                                        ? 'border-orange-500 bg-orange-50'
                                        : 'border-gray-200 hover:border-gray-300 bg-white'
                                    }`}
                                  >
                                    <input
                                      type="radio"
                                      name="contentType"
                                      value={type.id}
                                      checked={contentType === type.id}
                                      onChange={(e) => setContentType(e.target.value)}
                                      className="sr-only"
                                    />
                                    <span className="text-base">{type.icon}</span>
                                    <span className="text-xs font-medium text-gray-700">{type.label}</span>
                                    {contentType === type.id && (
                                      <Check size={14} className="ml-auto text-orange-500" />
                                    )}
                                  </label>
                                ))}
                              </div>
                            </div>

                            {/* 3. HUMEUR / SENTIMENT */}
                            <div>
                              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                                <span className="text-base">😊</span>
                                Comment tu te sens ?
                              </label>
                              <div className="flex flex-wrap gap-2">
                                {HUMEURS.map(humeur => (
                                  <button
                                    key={humeur.id}
                                    type="button"
                                    onClick={() => setSelectedMood(selectedMood === humeur.id ? '' : humeur.id)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                                      selectedMood === humeur.id
                                        ? 'bg-orange-500 text-white'
                                        : 'bg-white border border-gray-200 text-gray-600 hover:border-orange-300'
                                    }`}
                                  >
                                    <span>{humeur.emoji}</span>
                                    <span>{humeur.label}</span>
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* 4. AVEC QUI - Tagger des personnes */}
                            <div>
                              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                                <span className="text-base">👥</span>
                                Avec qui ?
                              </label>
                              <div className="relative">
                                <Users size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                  type="text"
                                  value={taggedPeople}
                                  onChange={(e) => setTaggedPeople(e.target.value)}
                                  placeholder="Tagger des personnes..."
                                  className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 bg-white"
                                />
                              </div>
                              <p className="text-xs text-gray-400 mt-1">
                                Mentionne les personnes qui t'ont aidé ou accompagné
                              </p>
                            </div>

                            {/* 5. GÉOLOCALISATION */}
                            <div>
                              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                                <span className="text-base">📍</span>
                                Localisation
                              </label>
                              <div className="relative">
                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                  type="text"
                                  value={postLocation}
                                  onChange={(e) => setPostLocation(e.target.value)}
                                  placeholder="Ajouter un lieu..."
                                  className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 bg-white"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Pro: Destination */}
                          {profileType === 'pro' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-3">
                                Destination
                              </label>
                              <div className="space-y-2">
                                {[
                                  { id: 'social', icon: '🌍', label: 'Réseau Social', desc: 'Visible par tous' },
                                  { id: 'pro', icon: '💼', label: 'Réseau Pro', desc: 'Visible par les pros uniquement' },
                                  { id: 'both', icon: '🌍💼', label: 'Les deux', desc: 'Visibilité maximale' },
                                ].map(opt => (
                                  <label 
                                    key={opt.id}
                                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                                      destination === opt.id 
                                        ? 'border-[#1E3A5F] bg-[#EEF2F8]' 
                                        : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                  >
                                    <input
                                      type="radio"
                                      name="destination"
                                      value={opt.id}
                                      checked={destination === opt.id}
                                      onChange={(e) => setDestination(e.target.value)}
                                      className="sr-only"
                                    />
                                    <span className="text-lg">{opt.icon}</span>
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-[#1E3A5F]">{opt.label}</p>
                                      <p className="text-xs text-gray-500">{opt.desc}</p>
                                    </div>
                                    {destination === opt.id && (
                                      <Check size={18} className="text-[#1E3A5F]" />
                                    )}
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Pro: Lien produit */}
                          {profileType === 'pro' && (
                            <div>
                              <div className="flex items-center justify-between">
                                <label className="text-sm font-medium text-gray-700">
                                  Ajouter un produit ou prestation ?
                                </label>
                                <button
                                  onClick={() => setShowProductLink(!showProductLink)}
                                  className={`w-11 h-6 rounded-full transition-colors ${
                                    showProductLink ? 'bg-[#1E3A5F]' : 'bg-gray-300'
                                  }`}
                                >
                                  <div className={`w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform ${
                                    showProductLink ? 'translate-x-5' : 'translate-x-0.5'
                                  }`} />
                                </button>
                              </div>
                              {showProductLink && (
                                <div className="mt-3 p-4 rounded-xl bg-[#FFF8F4] border border-[#FCE4D4]">
                                  <select className="w-full p-2.5 border border-[#FCE4D4] rounded-lg text-sm bg-white mb-3">
                                    <option>Choisir dans ma boutique</option>
                                  </select>
                                  <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                                      <Building2 size={20} className="text-gray-400" />
                                    </div>
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-gray-800">Nom du produit</p>
                                      <p className="text-xs text-[#F97316] font-semibold">À partir de 150€</p>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}

                      {/* FORMULAIRE CHANTIER */}
                      {activeForm === 'chantier' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Nom du chantier <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={chantierName}
                              onChange={(e) => setChantierName(e.target.value)}
                              placeholder="Ex: Rénovation salle de bain"
                              className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20 text-sm"
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Type de travaux</label>
                              <select
                                value={selectedMetier}
                                onChange={(e) => setSelectedMetier(e.target.value)}
                                className="w-full p-3 border border-gray-200 rounded-xl text-sm bg-white"
                              >
                                <option value="">Sélectionner</option>
                                {METIERS.map(m => <option key={m} value={m}>{m}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Ville</label>
                              <input
                                type="text"
                                value={chantierCity}
                                onChange={(e) => setChantierCity(e.target.value)}
                                placeholder="Ex: Nice"
                                className="w-full p-3 border border-gray-200 rounded-xl text-sm"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                            <textarea
                              value={postText}
                              onChange={(e) => setPostText(e.target.value.slice(0, 300))}
                              placeholder="Décris l'avancement du chantier..."
                              className="w-full min-h-[80px] p-3 border border-gray-200 rounded-xl text-sm resize-none"
                            />
                          </div>

                          <div 
                            className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center bg-gray-50 cursor-pointer"
                          >
                            <Upload size={24} className="mx-auto text-gray-400 mb-1" />
                            <p className="text-sm text-gray-600">Ajouter des photos</p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3">
                              Avancement : {chantierProgress}%
                            </label>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={chantierProgress}
                              onChange={(e) => setChantierProgress(Number(e.target.value))}
                              className="w-full h-2 rounded-full appearance-none cursor-pointer"
                              style={{
                                background: `linear-gradient(to right, ${accentColor} 0%, ${accentColor} ${chantierProgress}%, #e5e7eb ${chantierProgress}%, #e5e7eb 100%)`,
                              }}
                            />
                            <div className="flex justify-between text-xs text-gray-400 mt-1">
                              <span>Début</span>
                              <span>Terminé</span>
                            </div>
                          </div>
                        </>
                      )}

                      {/* FORMULAIRE SOUS-TRAITANT (Pro) */}
                      {activeForm === 'soustraitant' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Métier recherché <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={selectedMetier}
                              onChange={(e) => setSelectedMetier(e.target.value)}
                              className="w-full p-3 border border-gray-200 rounded-xl text-sm bg-white"
                            >
                              <option value="">Sélectionner un métier</option>
                              {METIERS.map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Ville</label>
                              <input
                                type="text"
                                value={chantierCity}
                                onChange={(e) => setChantierCity(e.target.value)}
                                placeholder="Ex: Nice"
                                className="w-full p-3 border border-gray-200 rounded-xl text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Rayon : {searchRadius} km
                              </label>
                              <input
                                type="range"
                                min="5"
                                max="100"
                                value={searchRadius}
                                onChange={(e) => setSearchRadius(Number(e.target.value))}
                                className="w-full h-2 rounded-full appearance-none cursor-pointer mt-3"
                                style={{
                                  background: `linear-gradient(to right, #1E3A5F 0%, #1E3A5F ${(searchRadius-5)/95*100}%, #e5e7eb ${(searchRadius-5)/95*100}%, #e5e7eb 100%)`,
                                }}
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Description de la mission</label>
                            <textarea
                              value={postText}
                              onChange={(e) => setPostText(e.target.value)}
                              placeholder="Décris la mission, les compétences requises..."
                              className="w-full min-h-[80px] p-3 border border-gray-200 rounded-xl text-sm resize-none"
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Date de début</label>
                              <input
                                type="date"
                                value={eventDate}
                                onChange={(e) => setEventDate(e.target.value)}
                                className="w-full p-3 border border-gray-200 rounded-xl text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Budget estimé</label>
                              <input
                                type="text"
                                placeholder="Ex: 500-800€"
                                className="w-full p-3 border border-gray-200 rounded-xl text-sm"
                              />
                            </div>
                          </div>

                          <div className="p-3 bg-blue-50 rounded-xl flex items-center gap-2 text-sm text-blue-700">
                            <AlertCircle size={16} />
                            <span>Cette publication sera visible uniquement sur le Réseau Pro</span>
                          </div>
                        </>
                      )}

                      {/* FORMULAIRE ÉVÉNEMENT (Pro) */}
                      {activeForm === 'evenement' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Titre de l'événement <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={chantierName}
                              onChange={(e) => setChantierName(e.target.value)}
                              placeholder="Ex: Formation DTU plomberie"
                              className="w-full p-3 border border-gray-200 rounded-xl text-sm"
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                              <input
                                type="date"
                                value={eventDate}
                                onChange={(e) => setEventDate(e.target.value)}
                                className="w-full p-3 border border-gray-200 rounded-xl text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Heure</label>
                              <input
                                type="time"
                                value={eventTime}
                                onChange={(e) => setEventTime(e.target.value)}
                                className="w-full p-3 border border-gray-200 rounded-xl text-sm"
                              />
                            </div>
                          </div>

                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                            <span className="text-sm font-medium text-gray-700">Événement en ligne</span>
                            <button
                              onClick={() => setEventOnline(!eventOnline)}
                              className={`w-11 h-6 rounded-full transition-colors ${
                                eventOnline ? 'bg-[#1E3A5F]' : 'bg-gray-300'
                              }`}
                            >
                              <div className={`w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform ${
                                eventOnline ? 'translate-x-5' : 'translate-x-0.5'
                              }`} />
                            </button>
                          </div>

                          {!eventOnline && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Lieu</label>
                              <input
                                type="text"
                                value={chantierCity}
                                onChange={(e) => setChantierCity(e.target.value)}
                                placeholder="Adresse de l'événement"
                                className="w-full p-3 border border-gray-200 rounded-xl text-sm"
                              />
                            </div>
                          )}

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                            <textarea
                              value={postText}
                              onChange={(e) => setPostText(e.target.value)}
                              placeholder="Décris l'événement..."
                              className="w-full min-h-[80px] p-3 border border-gray-200 rounded-xl text-sm resize-none"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Lien d'inscription (optionnel)</label>
                            <input
                              type="url"
                              placeholder="https://..."
                              className="w-full p-3 border border-gray-200 rounded-xl text-sm"
                            />
                          </div>
                        </>
                      )}

                      {/* FORMULAIRE RECHERCHE ARTISAN (Particulier) */}
                      {activeForm === 'recherche' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Quel type d'artisan cherches-tu ? <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={selectedMetier}
                              onChange={(e) => setSelectedMetier(e.target.value)}
                              className="w-full p-3 border border-gray-200 rounded-xl text-sm bg-white"
                            >
                              <option value="">Sélectionner un métier</option>
                              {METIERS.map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Ville</label>
                            <input
                              type="text"
                              value={chantierCity}
                              onChange={(e) => setChantierCity(e.target.value)}
                              placeholder="Ex: Nice"
                              className="w-full p-3 border border-gray-200 rounded-xl text-sm"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Décris ton projet</label>
                            <textarea
                              value={postText}
                              onChange={(e) => setPostText(e.target.value)}
                              placeholder="Détaille ton besoin pour attirer les bons artisans..."
                              className="w-full min-h-[100px] p-3 border border-gray-200 rounded-xl text-sm resize-none"
                            />
                          </div>

                          <div 
                            className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center bg-gray-50 cursor-pointer"
                          >
                            <Upload size={24} className="mx-auto text-gray-400 mb-1" />
                            <p className="text-sm text-gray-600">Ajouter des photos (optionnel)</p>
                          </div>
                        </>
                      )}

                      {/* ════════════════════════════════════════════════════════════════════════════
                          FORMULAIRE PETITE ANNONCE - Formulaire complet style Leboncoin
                      ════════════════════════════════════════════════════════════════════════════ */}
                      {activeForm === 'annonce' && (
                        <div className="space-y-4">
                          {/* Titre de l'annonce */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Titre de l'annonce <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={annonceTitle}
                              onChange={(e) => setAnnonceTitle(e.target.value.slice(0, 70))}
                              placeholder="Ex: Perceuse Bosch Professional GSB 18V neuve"
                              className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#F97316]/20 focus:border-[#F97316]"
                              data-testid="annonce-title-input"
                            />
                            <span className="text-[11px] text-gray-400 mt-1 block text-right">{annonceTitle.length}/70</span>
                          </div>

                          {/* Catégorie et Sous-catégorie */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Catégorie <span className="text-red-500">*</span>
                              </label>
                              <select
                                value={annonceCategory}
                                onChange={(e) => {
                                  setAnnonceCategory(e.target.value);
                                  setAnnonceSubcategory(''); // Reset subcategory when category changes
                                }}
                                className="w-full p-3 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-[#F97316]/20 focus:border-[#F97316]"
                                data-testid="annonce-category-select"
                              >
                                <option value="">Sélectionner un rayon</option>
                                {ANNONCE_CATEGORIES.map(cat => (
                                  <option key={cat.id} value={cat.id}>{cat.icon} {cat.label}</option>
                                ))}
                              </select>
                            </div>
                            
                            {/* Sous-catégorie (affichée si catégorie sélectionnée) */}
                            {annonceCategory && currentSubcategories.length > 0 && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Sous-catégorie
                                </label>
                                <select
                                  value={annonceSubcategory}
                                  onChange={(e) => setAnnonceSubcategory(e.target.value)}
                                  className="w-full p-3 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-[#F97316]/20 focus:border-[#F97316]"
                                  data-testid="annonce-subcategory-select"
                                >
                                  <option value="">Toutes les sous-catégories</option>
                                  {currentSubcategories.map(sub => (
                                    <option key={sub.id} value={sub.id}>{sub.label}</option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </div>

                          {/* Description */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Description <span className="text-red-500">*</span>
                            </label>
                            <textarea
                              value={postText}
                              onChange={(e) => setPostText(e.target.value.slice(0, 2000))}
                              placeholder="Décris ton article en détail : marque, modèle, dimensions, état, raison de la vente..."
                              className="w-full min-h-[120px] p-3 border border-gray-200 rounded-xl text-sm resize-none focus:ring-2 focus:ring-[#F97316]/20 focus:border-[#F97316]"
                              data-testid="annonce-description-input"
                            />
                            <span className="text-[11px] text-gray-400 mt-1 block text-right">{postText.length}/2000</span>
                          </div>

                          {/* Prix et État */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Prix <span className="text-red-500">*</span>
                              </label>
                              <div className="relative">
                                <input
                                  type="number"
                                  value={annoncePrice}
                                  onChange={(e) => setAnnoncePrice(e.target.value)}
                                  placeholder="0"
                                  className="w-full p-3 pr-8 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#F97316]/20 focus:border-[#F97316]"
                                  data-testid="annonce-price-input"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">€</span>
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                État <span className="text-red-500">*</span>
                              </label>
                              <select
                                value={annonceCondition}
                                onChange={(e) => setAnnonceCondition(e.target.value)}
                                className="w-full p-3 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-[#F97316]/20 focus:border-[#F97316]"
                                data-testid="annonce-condition-select"
                              >
                                <option value="">Sélectionner</option>
                                {ANNONCE_CONDITIONS.map(cond => (
                                  <option key={cond.id} value={cond.id}>{cond.label}</option>
                                ))}
                              </select>
                            </div>
                          </div>

                          {/* Localisation */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Localisation <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={chantierCity}
                              onChange={(e) => setChantierCity(e.target.value)}
                              placeholder="Ville ou code postal"
                              className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#F97316]/20 focus:border-[#F97316]"
                              data-testid="annonce-city-input"
                            />
                          </div>

                          {/* ════════════════════════════════════════════════════════════════════════════
                              UPLOAD PHOTOS - Avec miniatures et réorganisation
                          ════════════════════════════════════════════════════════════════════════════ */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Photos <span className="text-[#F97316] font-normal text-xs">(5x plus de vues !)</span>
                            </label>
                            
                            {/* Input caché */}
                            <input
                              ref={photoInputRef}
                              type="file"
                              accept="image/jpeg,image/png,image/webp"
                              multiple
                              className="hidden"
                              onChange={handleAnnoncePhotoUpload}
                              data-testid="annonce-photo-input"
                            />
                            
                            {/* Grille de photos */}
                            <div className="grid grid-cols-4 gap-2 mb-2">
                              {/* Photos existantes */}
                              {annoncePhotos.map((photo, index) => (
                                <div 
                                  key={photo.id}
                                  className={`relative aspect-square rounded-lg overflow-hidden border-2 ${
                                    index === 0 ? 'border-[#F97316] col-span-2 row-span-2' : 'border-gray-200'
                                  }`}
                                >
                                  <img 
                                    src={photo.preview} 
                                    alt={`Photo ${index + 1}`}
                                    className="w-full h-full object-cover"
                                  />
                                  
                                  {/* Badge photo principale */}
                                  {index === 0 && (
                                    <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-[#F97316] text-white text-[9px] font-bold rounded">
                                      PRINCIPALE
                                    </div>
                                  )}
                                  
                                  {/* Actions */}
                                  <div className="absolute inset-0 bg-black/0 hover:bg-black/40 transition-colors flex items-center justify-center gap-1 opacity-0 hover:opacity-100">
                                    {index !== 0 && (
                                      <button
                                        type="button"
                                        onClick={() => movePhotoToFirst(photo.id)}
                                        className="w-7 h-7 bg-white rounded-full flex items-center justify-center shadow-lg"
                                        title="Définir comme principale"
                                      >
                                        <Star size={14} className="text-[#F97316]" />
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => removeAnnoncePhoto(photo.id)}
                                      className="w-7 h-7 bg-white rounded-full flex items-center justify-center shadow-lg"
                                      title="Supprimer"
                                    >
                                      <Trash2 size={14} className="text-red-500" />
                                    </button>
                                  </div>
                                  
                                  {/* Numéro */}
                                  {index > 0 && (
                                    <div className="absolute bottom-1 right-1 w-5 h-5 bg-black/60 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                                      {index + 1}
                                    </div>
                                  )}
                                </div>
                              ))}
                              
                              {/* Bouton ajouter photo */}
                              {annoncePhotos.length < 10 && (
                                <button
                                  type="button"
                                  onClick={() => photoInputRef.current?.click()}
                                  className={`aspect-square rounded-lg border-2 border-dashed border-[#F97316]/40 bg-[#FFF8F4] hover:border-[#F97316] hover:bg-[#FFF0E6] transition-all flex flex-col items-center justify-center ${
                                    annoncePhotos.length === 0 ? 'col-span-2 row-span-2' : ''
                                  }`}
                                  data-testid="annonce-add-photo-btn"
                                >
                                  <Plus size={annoncePhotos.length === 0 ? 32 : 20} className="text-[#F97316] mb-1" />
                                  <span className={`text-[#F97316] font-medium ${annoncePhotos.length === 0 ? 'text-sm' : 'text-[10px]'}`}>
                                    {annoncePhotos.length === 0 ? 'Ajouter des photos' : 'Ajouter'}
                                  </span>
                                  {annoncePhotos.length === 0 && (
                                    <span className="text-[10px] text-gray-500 mt-1">Max 10 • JPG, PNG</span>
                                  )}
                                </button>
                              )}
                            </div>
                            
                            {/* Compteur */}
                            <p className="text-[11px] text-gray-400 text-right">
                              {annoncePhotos.length}/10 photos
                            </p>
                          </div>

                          {/* ════════════════════════════════════════════════════════════════════════════
                              OPTIONS DE REMISE - Main propre, Envoi, Point relais
                          ════════════════════════════════════════════════════════════════════════════ */}
                          <div className="space-y-3">
                            <p className="text-sm font-medium text-gray-700">Comment récupérer l'article ?</p>
                            
                            {/* Remise en main propre */}
                            <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-xl cursor-pointer hover:border-[#F97316]/50 hover:bg-[#FFF8F4]/50 transition-all">
                              <input
                                type="checkbox"
                                checked={annonceRemiseOptions.mainPropre}
                                onChange={(e) => setAnnonceRemiseOptions(prev => ({...prev, mainPropre: e.target.checked}))}
                                className="w-5 h-5 rounded border-gray-300 text-[#F97316] focus:ring-[#F97316] mt-0.5"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-lg">🤝</span>
                                  <span className="text-sm font-medium text-gray-800">Remise en main propre</span>
                                </div>
                                <p className="text-xs text-gray-500 mt-0.5 ml-7">
                                  L'acheteur vient chercher l'article chez toi
                                </p>
                              </div>
                            </label>

                            {/* Envoi possible */}
                            <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-xl cursor-pointer hover:border-[#F97316]/50 hover:bg-[#FFF8F4]/50 transition-all">
                              <input
                                type="checkbox"
                                checked={annonceRemiseOptions.envoi}
                                onChange={(e) => setAnnonceRemiseOptions(prev => ({...prev, envoi: e.target.checked}))}
                                className="w-5 h-5 rounded border-gray-300 text-[#F97316] focus:ring-[#F97316] mt-0.5"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-lg">📦</span>
                                  <span className="text-sm font-medium text-gray-800">Envoi par colis</span>
                                </div>
                                <p className="text-xs text-gray-500 mt-0.5 ml-7">
                                  Tu t'occupes de l'expédition
                                </p>
                                
                                {/* Frais de port si envoi activé */}
                                {annonceRemiseOptions.envoi && (
                                  <div className="mt-2 ml-7">
                                    <label className="text-xs text-gray-600 mb-1 block">Frais de port</label>
                                    <div className="flex gap-2">
                                      <button
                                        type="button"
                                        onClick={() => setAnnonceRemiseOptions(prev => ({...prev, fraisPort: 'offerts'}))}
                                        className={`px-3 py-1.5 text-xs rounded-lg border transition-all ${
                                          annonceRemiseOptions.fraisPort === 'offerts'
                                            ? 'bg-[#F97316] text-white border-[#F97316]'
                                            : 'bg-white text-gray-700 border-gray-200 hover:border-[#F97316]'
                                        }`}
                                      >
                                        🎁 Offerts
                                      </button>
                                      <div className="relative flex-1">
                                        <input
                                          type="number"
                                          value={annonceRemiseOptions.fraisPort === 'offerts' ? '' : annonceRemiseOptions.fraisPort}
                                          onChange={(e) => setAnnonceRemiseOptions(prev => ({...prev, fraisPort: e.target.value}))}
                                          placeholder="Montant"
                                          className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg pr-6 focus:ring-1 focus:ring-[#F97316] focus:border-[#F97316]"
                                        />
                                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">€</span>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </label>

                            {/* Point relais */}
                            <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-xl cursor-pointer hover:border-[#F97316]/50 hover:bg-[#FFF8F4]/50 transition-all">
                              <input
                                type="checkbox"
                                checked={annonceRemiseOptions.pointRelais}
                                onChange={(e) => setAnnonceRemiseOptions(prev => ({...prev, pointRelais: e.target.checked}))}
                                className="w-5 h-5 rounded border-gray-300 text-[#F97316] focus:ring-[#F97316] mt-0.5"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-lg">📍</span>
                                  <span className="text-sm font-medium text-gray-800">Point relais</span>
                                </div>
                                <p className="text-xs text-gray-500 mt-0.5 ml-7">
                                  Dépôt dans un point relais près de chez toi
                                </p>
                              </div>
                            </label>
                          </div>

                          {/* Options supplémentaires */}
                          <div className="space-y-3 p-4 bg-gray-50 rounded-xl">
                            <p className="text-sm font-medium text-gray-700 mb-2">Options supplémentaires</p>
                            
                            {/* Prix négociable */}
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-600">Prix négociable</span>
                              <button
                                type="button"
                                onClick={() => setAnnonceNegotiable(!annonceNegotiable)}
                                className={`w-11 h-6 rounded-full transition-colors ${
                                  annonceNegotiable ? 'bg-[#F97316]' : 'bg-gray-300'
                                }`}
                                data-testid="annonce-negotiable-toggle"
                              >
                                <div className={`w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform ${
                                  annonceNegotiable ? 'translate-x-5' : 'translate-x-0.5'
                                }`} />
                              </button>
                            </div>
                          </div>

                          {/* Info Cockpit */}
                          <div className="p-3 bg-[#FFF8F4] border border-[#F97316]/20 rounded-xl flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#F97316]/10 flex items-center justify-center flex-shrink-0">
                              <Home size={16} className="text-[#F97316]" />
                            </div>
                            <div className="text-sm">
                              <p className="font-medium text-gray-800">Ton annonce sera visible sur</p>
                              <p className="text-gray-500 text-xs mt-0.5">
                                Entre Particuliers • Marketplace • Et dans ton Cockpit
                              </p>
                            </div>
                          </div>

                          {/* ════════════════════════════════════════════════════════════════════════════
                              APERÇU EN TEMPS RÉEL - Carte style recherche
                          ════════════════════════════════════════════════════════════════════════════ */}
                          {(annonceTitle || annoncePrice) && (
                            <div className="mt-4">
                              <div className="flex items-center gap-2 mb-3">
                                <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                                  <Check size={12} className="text-green-600" />
                                </div>
                                <p className="text-sm font-medium text-gray-700">Aperçu de ton annonce</p>
                              </div>
                              
                              {/* Card Preview */}
                              <div 
                                className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
                                data-testid="annonce-preview-card"
                              >
                                {/* Image - avec preview si photo uploadée */}
                                <div className="aspect-[4/3] bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center relative overflow-hidden">
                                  {annoncePhotos.length > 0 ? (
                                    <img 
                                      src={annoncePhotos[0].preview} 
                                      alt="Preview" 
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="text-center">
                                      <Camera size={32} className="mx-auto text-gray-300 mb-2" />
                                      <p className="text-xs text-gray-400">Photo de l'article</p>
                                    </div>
                                  )}
                                  
                                  {/* Compteur photos */}
                                  {annoncePhotos.length > 1 && (
                                    <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/60 text-white rounded-md text-[10px] font-medium">
                                      📷 {annoncePhotos.length}
                                    </div>
                                  )}
                                  
                                  {/* Badge état */}
                                  {annonceCondition && (
                                    <div className="absolute top-2 left-2 px-2 py-1 bg-white/90 backdrop-blur-sm rounded-md text-[10px] font-medium text-gray-700 shadow-sm">
                                      {ANNONCE_CONDITIONS.find(c => c.id === annonceCondition)?.label || annonceCondition}
                                    </div>
                                  )}
                                  
                                  {/* Badges de remise */}
                                  <div className="absolute top-2 right-2 flex flex-col gap-1">
                                    {annonceRemiseOptions.envoi && (
                                      <div className="px-2 py-1 bg-blue-500 text-white rounded-md text-[10px] font-medium shadow-sm">
                                        📦 Envoi
                                      </div>
                                    )}
                                    {annonceRemiseOptions.mainPropre && (
                                      <div className="px-2 py-1 bg-green-500 text-white rounded-md text-[10px] font-medium shadow-sm">
                                        🤝 Main propre
                                      </div>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Content */}
                                <div className="p-3">
                                  {/* Prix */}
                                  <div className="flex items-baseline gap-2 mb-1">
                                    <span className="text-xl font-bold text-[#1E3A5F]">
                                      {annoncePrice ? `${Number(annoncePrice).toLocaleString('fr-FR')} €` : '— €'}
                                    </span>
                                    {annonceNegotiable && (
                                      <span className="text-[10px] text-[#F97316] font-medium bg-[#FFF8F4] px-1.5 py-0.5 rounded">
                                        Négociable
                                      </span>
                                    )}
                                  </div>
                                  
                                  {/* Titre */}
                                  <h3 className="text-sm font-semibold text-gray-800 line-clamp-2 mb-2">
                                    {annonceTitle || 'Titre de ton annonce...'}
                                  </h3>
                                  
                                  {/* Catégorie & Localisation */}
                                  <div className="flex items-center gap-2 text-xs text-gray-500">
                                    {annonceCategory && (
                                      <>
                                        <span>{ANNONCE_CATEGORIES.find(c => c.id === annonceCategory)?.icon}</span>
                                        <span>{ANNONCE_CATEGORIES.find(c => c.id === annonceCategory)?.label}</span>
                                        <span>•</span>
                                      </>
                                    )}
                                    <span className="flex items-center gap-1">
                                      📍 {chantierCity || 'Localisation'}
                                    </span>
                                  </div>
                                  
                                  {/* Footer */}
                                  <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100">
                                    <div className="flex items-center gap-2">
                                      <div className="w-6 h-6 rounded-full bg-[#F97316] flex items-center justify-center text-white text-[10px] font-bold">
                                        {localStorage.getItem('buyer_user') 
                                          ? JSON.parse(localStorage.getItem('buyer_user')).name?.charAt(0)?.toUpperCase() || 'T' 
                                          : 'T'}
                                      </div>
                                      <span className="text-[11px] text-gray-500">Toi • Aujourd'hui</span>
                                    </div>
                                    <button className="text-gray-400 hover:text-red-500 transition-colors">
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              </div>
                              
                              <p className="text-center text-[11px] text-gray-400 mt-2">
                                Voici comment ton annonce apparaîtra dans les résultats
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Boutons d'action */}
                      <div className="flex gap-3 pt-2">
                        {/* Bouton Enregistrer brouillon */}
                        {hasFormContent && (
                          <button
                            onClick={handleSaveDraft}
                            className="flex-1 py-3.5 rounded-xl font-semibold text-sm transition-all border-2 border-gray-200 text-gray-700 hover:border-[#1E3A5F] hover:text-[#1E3A5F] flex items-center justify-center gap-2"
                            data-testid="save-draft-btn"
                          >
                            <Save size={18} />
                            Enregistrer
                          </button>
                        )}

                        {/* Bouton Publier */}
                        <button
                          onClick={handlePublish}
                          disabled={
                            (activeForm === 'annonce' 
                              ? (!annonceTitle || !annonceCategory || !annoncePrice || !annonceCondition || !chantierCity)
                              : (!postText && !chantierName)
                            ) || isPublishing
                          }
                          className={`${hasFormContent ? 'flex-1' : 'w-full'} py-3.5 rounded-xl text-white font-bold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
                          style={{ backgroundColor: '#F97316' }}
                          data-testid="publish-submit-btn"
                        >
                          {isPublishing ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              {activeForm === 'annonce' ? 'Dépôt en cours...' : 'Publication...'}
                            </>
                          ) : (
                            activeForm === 'annonce' ? 'Déposer mon annonce' : 'Publier maintenant'
                          )}
                        </button>
                      </div>
                      
                      {/* Prompt d'abonnement pour les non-abonnés */}
                      {showSubscribePrompt && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-4 p-4 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl"
                        >
                          <div className="text-center">
                            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                              <Star size={24} className="text-orange-500" />
                            </div>
                            <h4 className="font-semibold text-gray-900 mb-2">Passe à l'action !</h4>
                            <p className="text-sm text-gray-600 mb-4">
                              Pour publier et interagir avec la communauté, crée ton compte gratuitement.
                            </p>
                            <div className="flex gap-2">
                              <button
                                onClick={() => setShowSubscribePrompt(false)}
                                className="flex-1 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium text-sm hover:bg-gray-50"
                              >
                                Plus tard
                              </button>
                              <button
                                onClick={handleSubscribe}
                                className="flex-1 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold text-sm transition-colors"
                              >
                                S'inscrire
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </div>
                  </>
                )}
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
